import { ref, onBeforeUnmount } from "@vue/composition-api";
import axios from "@axios";
import { openContactsTemplate } from "./readContactsTemplate";

const SHEET_NS = "http://schemas.openxmlformats.org/spreadsheetml/2006/main";

// Store XML files in an uncompressed ZIP without additional dependencies.
export function zipTemplate(files) {
  const localParts = [];
  const centralParts = [];
  let offset = 0;
  let centralSize = 0;
  files.forEach(([name, xml]) => {
    const filename = new TextEncoder().encode(name);
    const data = new TextEncoder().encode(xml);
    let crc = 0xffffffff;
    data.forEach(byte => {
      crc ^= byte;
      for (let bit = 0; bit < 8; bit += 1) crc = (crc >>> 1) ^ ((crc & 1) ? 0xedb88320 : 0);
    });
    crc = (crc ^ 0xffffffff) >>> 0;
    const local = new Uint8Array(30 + filename.length);
    const central = new Uint8Array(46 + filename.length);
    const l = new DataView(local.buffer);
    const c = new DataView(central.buffer);
    l.setUint32(0, 0x04034b50, true);
    l.setUint16(4, 20, true);
    l.setUint16(12, 33, true);
    l.setUint32(14, crc, true);
    l.setUint32(18, data.length, true);
    l.setUint32(22, data.length, true);
    l.setUint16(26, filename.length, true);
    local.set(filename, 30);
    c.setUint32(0, 0x02014b50, true);
    c.setUint16(4, 20, true);
    c.setUint16(6, 20, true);
    c.setUint16(14, 33, true);
    c.setUint32(16, crc, true);
    c.setUint32(20, data.length, true);
    c.setUint32(24, data.length, true);
    c.setUint16(28, filename.length, true);
    c.setUint32(42, offset, true);
    central.set(filename, 46);
    localParts.push(local, data);
    centralParts.push(central);
    offset += local.length + data.length;
    centralSize += central.length;
  });
  const end = new Uint8Array(22);
  const view = new DataView(end.buffer);
  view.setUint32(0, 0x06054b50, true);
  view.setUint16(8, files.length, true);
  view.setUint16(10, files.length, true);
  view.setUint32(12, centralSize, true);
  view.setUint32(16, offset, true);
  return new Blob([...localParts, ...centralParts, end], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
}

export async function buildContactsTemplate(file, sections) {
  const { readXml } = await openContactsTemplate(file);
  const paths = ["[Content_Types].xml", "_rels/.rels", "xl/workbook.xml", "xl/_rels/workbook.xml.rels", "xl/styles.xml", "xl/worksheets/sheet1.xml"];
  const documents = await Promise.all(paths.map(path => readXml(path)));
  const add = (parent, ns, tag, attributes = {}, text) => {
    const node = parent.ownerDocument.createElementNS(ns, tag);
    Object.entries(attributes).forEach(([key, value]) => node.setAttribute(key, value));
    if (text !== undefined) node.textContent = text;
    parent.appendChild(node);
    return node;
  };
  const [types, , workbook, relationships, , sheet] = documents;
  add(types.documentElement, types.documentElement.namespaceURI, "Override", {
    PartName: "/xl/worksheets/sections.xml",
    ContentType: "application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml",
  });
  add(relationships.documentElement, relationships.documentElement.namespaceURI, "Relationship", {
    Id: "contactsSections", Type: "http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet", Target: "worksheets/sections.xml",
  });
  const sectionSheet = add(workbook.getElementsByTagNameNS(SHEET_NS, "sheets")[0], SHEET_NS, "sheet", {
    name: "Secciones", sheetId: "2", state: "hidden",
  });
  sectionSheet.setAttributeNS("http://schemas.openxmlformats.org/officeDocument/2006/relationships", "r:id", "contactsSections");
  const definitions = add(workbook.documentElement, SHEET_NS, "definedNames");
  add(definitions, SHEET_NS, "definedName", { name: "ContactSections" }, `'Secciones'!$A$1:$A$${sections.length}`);
  const validations = add(sheet.documentElement, SHEET_NS, "dataValidations", { count: "1" });
  const margins = sheet.getElementsByTagNameNS(SHEET_NS, "pageMargins")[0];
  if (margins) sheet.documentElement.insertBefore(validations, margins);
  const validation = add(validations, SHEET_NS, "dataValidation", {
    type: "list", allowBlank: "1", showErrorMessage: "1", errorStyle: "stop", sqref: "A2:A1048576",
  });
  add(validation, SHEET_NS, "formula1", {}, "ContactSections");
  const catalog = new DOMParser().parseFromString(`<worksheet xmlns="${SHEET_NS}"><sheetData/></worksheet>`, "application/xml");
  const data = catalog.documentElement.firstChild;
  sections.forEach((name, index) => {
    const row = add(data, SHEET_NS, "row", { r: index + 1 });
    const cell = add(row, SHEET_NS, "c", { r: `A${index + 1}`, t: "inlineStr" });
    add(add(cell, SHEET_NS, "is"), SHEET_NS, "t", {}, name);
  });
  const serializer = new XMLSerializer();
  return zipTemplate([
    ...paths.map((path, index) => [path, serializer.serializeToString(documents[index])]),
    ["xl/worksheets/sections.xml", serializer.serializeToString(catalog)],
  ]);
}

export default function useContactsTemplateDownload() {
  const downloadingTemplate = ref(false);
  const templateDownloadError = ref("");
  let disposed = false;
  onBeforeUnmount(() => { disposed = true; });
  const downloadContactsTemplate = async () => {
    if (downloadingTemplate.value) return;
    downloadingTemplate.value = true;
    templateDownloadError.value = "";
    try {
      const names = new Set();
      for (let page = 1; ; page += 1) {
        const { data } = await axios.get("v1/teams", { params: { "page[n]": page, "page[s]": 100 } });
        if (disposed) return;
        if (!Array.isArray(data.data)) throw new Error();
        data.data.forEach(team => {
          if (typeof team.name === "string" && team.name.trim()) names.add(team.name.trim());
        });
        if (data.data.length < 100) break;
      }
      if (!names.size) {
        templateDownloadError.value = "contacts_template_no_sections";
        return;
      }
      const response = await fetch("/units_contacts_template.xlsx");
      if (!response.ok) throw new Error();
      const file = new File([await response.blob()], "units_contacts_template.xlsx");
      const template = await buildContactsTemplate(file, Array.from(names));
      if (disposed) return;
      const url = URL.createObjectURL(template);
      const link = document.createElement("a");
      link.href = url;
      link.download = "units_contacts_template.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) {
      templateDownloadError.value = "contacts_template_download_error";
    } finally {
      downloadingTemplate.value = false;
    }
  };
  return { downloadingTemplate, templateDownloadError, downloadContactsTemplate };
}
